import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { utils } from '../../utils';
import { usePermissions } from '../../hooks';
import { SystemModule } from '../../types/permissions';
import {
  listarPedidosAgrupados,
  getEstatisticasEtiquetas,
  deletarPedidoCompleto,
  PedidoAgrupado,
  EtiquetaEstatisticas,
  EtiquetaStatus,
  STATUS_LABELS,
  STATUS_COLORS,
  STATUS_ICONS
} from '../../services/etiquetas';
import { listarClientes, Cliente } from '../../services/clientes';
import FilterPanel from '../../components/Common/FilterPanel';
import ResultsCounter from '../../components/Common/ResultsCounter';
import ImportarEtiquetasModal from './ImportarEtiquetasModal';
import { getHojeISO, toInicioDiaISO, toFimDiaISO } from '../../utils/dateHelpers';

const EtiquetasList: React.FC = () => {
  const navigate = useNavigate();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [pedidos, setPedidos] = useState<PedidoAgrupado[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<{ ids: number[]; pedido: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [estatisticas, setEstatisticas] = useState<EtiquetaEstatisticas | null>(null);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [clienteFilter, setClienteFilter] = useState('');
  const [dataInicio, setDataInicio] = useState<string>(getHojeISO());
  const [dataFim, setDataFim] = useState<string>(getHojeISO());
  const [showFilters, setShowFilters] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [expandedPedidos, setExpandedPedidos] = useState<Set<string>>(new Set());
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 0 });

  const fetchPedidos = useCallback(async (page = 1) => {
    setIsLoading(true);
    try {
      // Busca por identificador desconsidera filtro de data e pesquisa todo
      // o historico - usuario espera encontrar o item independente do periodo.
      const buscaAtiva = Boolean(busca && busca.trim());
      const dataInicioParam = buscaAtiva ? undefined : toInicioDiaISO(dataInicio);
      const dataFimParam = buscaAtiva ? undefined : toFimDiaISO(dataFim);

      const [pedResult, statsResult] = await Promise.all([
        listarPedidosAgrupados({
          page,
          limit: 20,
          busca: busca || undefined,
          status: statusFilter || undefined,
          cliente_id: clienteFilter ? parseInt(clienteFilter) : undefined,
          data_inicio: dataInicioParam,
          data_fim: dataFimParam
        }),
        getEstatisticasEtiquetas({
          cliente_id: clienteFilter ? parseInt(clienteFilter) : undefined,
          data_inicio: dataInicioParam,
          data_fim: dataFimParam
        })
      ]);

      if (pedResult.success) {
        setPedidos(pedResult.dados);
        setPagination(pedResult.paginacao);
      }
      if (statsResult.success) {
        setEstatisticas(statsResult.dados);
      }
    } catch (error: any) {
      console.error('Erro ao carregar pedidos:', error);
      utils.showNotification(error.message || 'Erro ao carregar pedidos', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [busca, statusFilter, clienteFilter, dataInicio, dataFim]);

  useEffect(() => {
    fetchPedidos();
  }, [fetchPedidos]);

  useEffect(() => {
    const fetchClientes = async () => {
      try {
        const result = await listarClientes({ limit: 100, ativo: 'true' });
        if (result.success) {
          setClientes(result.clientes);
        }
      } catch (error) {
        console.error('Erro ao carregar clientes:', error);
      }
    };
    fetchClientes();
  }, []);

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  const formatCurrency = (value?: number | string): string => {
    if (!value && value !== 0) return '-';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '-';
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const formatQuantity = (value?: string | number): string => {
    if (value === null || value === undefined || value === '') return '-';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return String(value);
    // Se é inteiro, mostra sem decimais. Se tem decimais, mostra até 2 casas.
    if (Number.isInteger(num)) return num.toLocaleString('pt-BR');
    return num.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  const toggleExpand = (pedidoKey: string) => {
    setExpandedPedidos(prev => {
      const next = new Set(prev);
      if (next.has(pedidoKey)) {
        next.delete(pedidoKey);
      } else {
        next.add(pedidoKey);
      }
      return next;
    });
  };

  const handleImportSuccess = () => {
    setShowImportModal(false);
    fetchPedidos();
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setIsDeleting(true);
    try {
      const response = await deletarPedidoCompleto(deleteConfirm.ids);
      if (response.success) {
        utils.showNotification('Pedido excluído com sucesso', { type: 'success' });
        setDeleteConfirm(null);
        fetchPedidos(pagination.page);
      }
    } catch (error: any) {
      utils.showNotification(error.message || 'Erro ao excluir pedido', { type: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const statusOptions: EtiquetaStatus[] = [
    'pendente', 'pre_carregamento', 'carregada', 'em_transito',
    'descarregada', 'conferida', 'divergente', 'com_ocorrencia'
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1><i className="fas fa-barcode"></i> Pedidos</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            className="btn btn-outline"
            onClick={() => setShowFilters(!showFilters)}
          >
            <i className="fas fa-filter"></i> Filtros
          </button>
          {canCreate(SystemModule.CONFERENCIA_CARGA) && (
            <>
              <button
                className="btn btn-outline"
                onClick={() => setShowImportModal(true)}
              >
                <i className="fas fa-file-import"></i> Importar
              </button>
              <button
                className="btn btn-primary"
                onClick={() => navigate('/pedidos/novo')}
              >
                <i className="fas fa-plus"></i> Novo Pedido
              </button>
            </>
          )}
        </div>
      </div>

      {/* Cards de Estatísticas */}
      {estatisticas && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '12px',
          marginBottom: '20px'
        }}>
          <StatCard label="Total" value={estatisticas.total} color="#374151" icon="fas fa-tags" />
          <StatCard label="Pendentes" value={estatisticas.pendentes} color={STATUS_COLORS.pendente} icon={STATUS_ICONS.pendente} />
          <StatCard label="Pré Carregamento" value={estatisticas.pre_carregamento} color={STATUS_COLORS.pre_carregamento} icon={STATUS_ICONS.pre_carregamento} />
          <StatCard label="Conferidas" value={estatisticas.conferidas} color={STATUS_COLORS.conferida} icon={STATUS_ICONS.conferida} />
          <StatCard label="Carregadas" value={estatisticas.carregadas} color={STATUS_COLORS.carregada} icon={STATUS_ICONS.carregada} />
          <StatCard label="Em Trânsito" value={estatisticas.em_transito} color={STATUS_COLORS.em_transito} icon={STATUS_ICONS.em_transito} />
          <StatCard label="Descarregadas" value={estatisticas.descarregadas} color={STATUS_COLORS.descarregada} icon={STATUS_ICONS.descarregada} />
          <StatCard label="Divergentes" value={estatisticas.divergentes} color={STATUS_COLORS.divergente} icon={STATUS_ICONS.divergente} />
        </div>
      )}

      {/* Filtros */}
      <FilterPanel
        fields={[
          {
            type: 'date',
            label: 'Data Inicial',
            value: dataInicio,
            onChange: setDataInicio
          },
          {
            type: 'date',
            label: 'Data Final',
            value: dataFim,
            onChange: setDataFim
          },
          {
            type: 'text',
            label: 'Busca',
            value: busca,
            onChange: setBusca,
            placeholder: 'NF-e, número do pedido...'
          },
          {
            type: 'select',
            label: 'Status',
            value: statusFilter,
            onChange: setStatusFilter,
            options: statusOptions.map(s => ({ value: s, label: STATUS_LABELS[s] }))
          },
          {
            type: 'select',
            label: 'Cliente',
            value: clienteFilter,
            onChange: setClienteFilter,
            options: clientes.map(c => ({ value: String(c.id), label: c.razao_social }))
          }
        ]}
        onFilter={() => fetchPedidos(1)}
        onClear={() => {
          setBusca('');
          setStatusFilter('');
          setClienteFilter('');
          setDataInicio(getHojeISO());
          setDataFim(getHojeISO());
        }}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
      />

      <ResultsCounter displayedItems={pedidos.length} totalItems={pagination.total} label="pedidos" />

      {/* Tabela */}
      <div style={{ overflowX: 'auto', marginBottom: '1.5rem', position: 'relative', zIndex: 0 }}>
        <table className="data-table" style={{ minWidth: '1000px' }}>
          <thead>
            <tr>
              <th style={{ width: '40px', padding: '12px 8px' }}></th>
              <th style={{ whiteSpace: 'nowrap' }}>Pedido</th>
              <th style={{ whiteSpace: 'nowrap' }}>Status</th>
              <th style={{ whiteSpace: 'nowrap' }}>NF-e</th>
              <th style={{ whiteSpace: 'nowrap' }}>Série</th>
              <th style={{ whiteSpace: 'nowrap' }}>Emitente</th>
              <th style={{ whiteSpace: 'nowrap' }}>CNPJ</th>
              <th style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>Itens</th>
              <th style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>Peso</th>
              <th style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>Volumes</th>
              <th style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>Valor NF-e</th>
              <th style={{ whiteSpace: 'nowrap' }}>Data Criação</th>
              <th style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && pedidos.length === 0 ? (
              <tr>
                <td colSpan={13} style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="loading-spinner-large"></div>
                  <p>Carregando pedidos...</p>
                </td>
              </tr>
            ) : pedidos.length === 0 ? (
              <tr>
                <td colSpan={13} className="table-empty-state">
                  <i className="fas fa-file-invoice" style={{ fontSize: '48px', marginBottom: '16px', display: 'block', opacity: 0.3 }}></i>
                  <p>Nenhum pedido encontrado no período selecionado</p>
                  <p style={{ fontSize: '14px' }}>Ajuste o filtro de datas ou importe pedidos a partir de arquivos XML (NF-e) ou EDI</p>
                </td>
              </tr>
            ) : (
              pedidos.map((pedido) => {
                const isExpanded = expandedPedidos.has(pedido.pedido_key);
                const statusKey = pedido.status as EtiquetaStatus;
                return (
                  <React.Fragment key={pedido.pedido_key}>
                    {/* Linha principal do pedido */}
                    <tr
                      style={{ cursor: 'pointer', backgroundColor: isExpanded ? '#f0f9ff' : undefined }}
                      onClick={() => toggleExpand(pedido.pedido_key)}
                    >
                      <td style={{ textAlign: 'center', padding: '12px 8px' }}>
                        <i
                          className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`}
                          style={{ color: '#6b7280', fontSize: '12px', transition: 'transform 0.2s' }}
                        ></i>
                      </td>
                      <td style={{ fontWeight: 600, fontFamily: 'monospace', color: '#1e40af' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                          {pedido.numero_pedido || '-'}
                          {pedido.tem_ocorrencia && (
                            <span
                              title="Pedido com ocorrência"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '2px 7px',
                                borderRadius: '10px',
                                fontSize: '10px',
                                fontWeight: 700,
                                color: '#fff',
                                backgroundColor: '#dc2626',
                                whiteSpace: 'nowrap',
                                lineHeight: 1.4
                              }}
                            >
                              <i className="fas fa-exclamation-circle" style={{ fontSize: '9px' }}></i>
                              Ocorrência
                            </span>
                          )}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 600,
                          color: '#fff',
                          backgroundColor: STATUS_COLORS[statusKey] || '#6b7280',
                          whiteSpace: 'nowrap'
                        }}>
                          <i className={STATUS_ICONS[statusKey] || 'fas fa-circle'} style={{ fontSize: '10px' }}></i>
                          {STATUS_LABELS[statusKey] || pedido.status}
                        </span>
                      </td>
                      <td style={{ fontWeight: 600, color: '#111827' }}>{pedido.numero_nfe || '-'}</td>
                      <td>{pedido.serie_nfe || '-'}</td>
                      <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {pedido.emitente_nome || pedido.cliente_nome || '-'}
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: '12px', whiteSpace: 'nowrap' }}>{
                        pedido.emitente_cnpj
                          ? pedido.emitente_cnpj.length === 14
                            ? pedido.emitente_cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
                            : pedido.emitente_cnpj.length === 11
                              ? pedido.emitente_cnpj.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
                              : pedido.emitente_cnpj
                          : '-'
                      }</td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          fontSize: '12px',
                          fontWeight: 600,
                          color: '#1d4ed8',
                          backgroundColor: '#dbeafe'
                        }}>
                          <i className="fas fa-box" style={{ fontSize: '10px' }}></i>
                          {pedido.total_itens}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {pedido.peso_total ? `${parseFloat(String(pedido.peso_total)).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 3 })} kg` : '-'}
                      </td>
                      <td style={{ textAlign: 'center' }}>{pedido.volumes || '-'}</td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 500 }}>{formatCurrency(pedido.valor_nfe)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDate(pedido.criado_em)}</td>
                      <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                          {pedido.itens[0]?.id && (
                            <button
                              className="btn btn-outline btn-sm"
                              title="Visualizar"
                              onClick={() => navigate(`/pedidos/${pedido.itens[0].id}`)}
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                          )}
                          {canUpdate(SystemModule.CONFERENCIA_CARGA) && pedido.itens[0]?.id && (
                            <button
                              className="btn btn-outline btn-sm"
                              title="Editar"
                              onClick={() => navigate(`/pedidos/${pedido.itens[0].id}/editar`)}
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                          )}
                          {canDelete(SystemModule.CONFERENCIA_CARGA) && pedido.itens[0]?.id && (
                            <button
                              className="btn btn-outline btn-sm"
                              title="Excluir"
                              onClick={() => setDeleteConfirm({ ids: pedido.itens.map(item => item.id).filter(Boolean), pedido: pedido.numero_pedido || pedido.numero_nfe || String(pedido.itens[0].id) })}
                              style={{ padding: '4px 8px', fontSize: '12px', color: '#ef4444', borderColor: '#fecaca' }}
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Linhas expandidas com detalhes dos itens e etiquetas */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={13} style={{ padding: 0, backgroundColor: '#f8fafc' }}>
                          <div style={{ padding: '12px 16px 12px 52px' }}>
                            {/* Seção: Itens do Pedido (produtos da NF-e) */}
                            {(() => {
                              const itensComProduto = pedido.itens.filter((i: any) =>
                                i.codigo_produto || i.descricao_produto || i.quantidade || i.valor_total_produto
                              );
                              return itensComProduto.length > 0 ? (
                                <>
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginBottom: '10px',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: '#374151'
                                  }}>
                                    <i className="fas fa-box-open" style={{ color: '#3b82f6' }}></i>
                                    Itens do Pedido ({itensComProduto.length} {itensComProduto.length === 1 ? 'item' : 'itens'})
                                  </div>
                                  <table className="data-table" style={{
                                    backgroundColor: '#fff',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                    marginBottom: '16px'
                                  }}>
                                    <thead>
                                      <tr style={{ backgroundColor: '#f1f5f9' }}>
                                        <th style={{ padding: '8px 12px', fontSize: '12px' }}>SKU</th>
                                        <th style={{ padding: '8px 12px', fontSize: '12px' }}>Descrição</th>
                                        <th style={{ padding: '8px 12px', fontSize: '12px' }}>NCM</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '12px' }}>Qtd</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '12px' }}>Valor Unit.</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '12px' }}>Valor Total</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {itensComProduto.map((item: any, idx: number) => (
                                        <tr key={`item-${item.id || idx}`}>
                                          <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontWeight: 500, color: '#1e40af' }}>
                                            {item.codigo_produto || '-'}
                                          </td>
                                          <td style={{ padding: '8px 12px', color: '#111827' }}>
                                            {item.descricao_produto || '-'}
                                          </td>
                                          <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: '12px', color: '#6b7280' }}>
                                            {item.ncm || '-'}
                                          </td>
                                          <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 500 }}>
                                            {formatQuantity(item.quantidade)}
                                          </td>
                                          <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                                            {formatCurrency(item.valor_unitario)}
                                          </td>
                                          <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#059669' }}>
                                            {formatCurrency(item.valor_total_produto)}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </>
                              ) : null;
                            })()}

                            {/* Seção: Etiquetas (labels com status de rastreamento) */}
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              marginBottom: '10px',
                              fontSize: '13px',
                              fontWeight: 600,
                              color: '#374151'
                            }}>
                              <i className="fas fa-tags" style={{ color: '#8b5cf6' }}></i>
                              Etiquetas ({pedido.itens.length} {pedido.itens.length === 1 ? 'etiqueta' : 'etiquetas'})
                              {(() => {
                                const bipadas = pedido.itens.filter((i: any) => i.status !== 'pendente').length;
                                return bipadas > 0 ? (
                                  <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: '8px' }}>
                                    - {bipadas}/{pedido.itens.length} bipadas
                                  </span>
                                ) : null;
                              })()}
                            </div>
                            <table className="data-table" style={{
                              backgroundColor: '#fff',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                            }}>
                              <thead>
                                <tr style={{ backgroundColor: '#f5f3ff' }}>
                                  <th style={{ padding: '8px 12px', fontSize: '12px', textAlign: 'center' }}>Vol.</th>
                                  <th style={{ padding: '8px 12px', fontSize: '12px' }}>Código de Barras</th>
                                  <th style={{ padding: '8px 12px', fontSize: '12px', textAlign: 'center' }}>Palete</th>
                                  <th style={{ padding: '8px 12px', fontSize: '12px' }}>Doca</th>
                                  <th style={{ padding: '8px 12px', fontSize: '12px' }}>Placa</th>
                                  <th style={{ padding: '8px 12px', fontSize: '12px' }}>Conferente</th>
                                  <th style={{ padding: '8px 12px', fontSize: '12px' }}>Filial</th>
                                  <th style={{ padding: '8px 12px', fontSize: '12px' }}>Status</th>
                                  <th style={{ padding: '8px 12px', fontSize: '12px', textAlign: 'center' }}>Bipada</th>
                                </tr>
                              </thead>
                              <tbody>
                                {pedido.itens.map((item: any, idx: number) => {
                                  const itemStatus = item.status as EtiquetaStatus;
                                  const foiBipada = item.status !== 'pendente';
                                  return (
                                  <tr key={`etiq-${item.id || idx}`}>
                                    <td style={{ padding: '8px 12px', textAlign: 'center', fontFamily: 'monospace', fontWeight: 600, color: '#6366f1' }}>
                                      {item.volume_atual || '-'}
                                    </td>
                                    <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: '12px', color: '#374151' }}>
                                      {item.codigo_barras || '-'}
                                    </td>
                                    <td style={{ padding: '8px 12px', textAlign: 'center', fontFamily: 'monospace', fontWeight: 600, color: '#0891b2' }}>
                                      {item.numero_palete ? `#${item.numero_palete}` : '-'}
                                    </td>
                                    <td style={{ padding: '8px 12px', fontSize: '12px', color: '#374151', fontWeight: 500 }}>
                                      {item.doca || '-'}
                                    </td>
                                    <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: '12px', fontWeight: 600, color: '#7c3aed', whiteSpace: 'nowrap' }}>
                                      {item.placa_veiculo || '-'}
                                    </td>
                                    <td style={{ padding: '8px 12px', fontSize: '12px', color: '#374151' }}>
                                      {item.conferente_nome || '-'}
                                    </td>
                                    <td style={{ padding: '8px 12px', fontSize: '12px', color: '#374151' }}>
                                      {item.filial_nome || '-'}
                                    </td>
                                    <td style={{ padding: '8px 12px' }}>
                                      <span style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                                        padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
                                        color: '#fff', backgroundColor: STATUS_COLORS[itemStatus] || '#6b7280', whiteSpace: 'nowrap'
                                      }}>
                                        <i className={STATUS_ICONS[itemStatus] || 'fas fa-circle'} style={{ fontSize: '8px' }}></i>
                                        {STATUS_LABELS[itemStatus] || item.status || '-'}
                                      </span>
                                    </td>
                                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                      {foiBipada ? (
                                        <i className="fas fa-check-circle" style={{ color: '#10b981', fontSize: '14px' }} title={item.codigo_barras || 'Bipada'}></i>
                                      ) : (
                                        <i className="fas fa-times-circle" style={{ color: '#ef4444', fontSize: '14px' }} title="Não bipada"></i>
                                      )}
                                    </td>
                                  </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Paginação */}
      {pagination.totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px' }}>
          <button
            className="btn btn-outline btn-sm"
            disabled={pagination.page <= 1}
            onClick={() => fetchPedidos(pagination.page - 1)}
          >
            <i className="fas fa-chevron-left"></i>
          </button>
          <span style={{ display: 'flex', alignItems: 'center', padding: '0 12px', fontSize: '14px' }}>
            Página {pagination.page} de {pagination.totalPages}
          </span>
          <button
            className="btn btn-outline btn-sm"
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => fetchPedidos(pagination.page + 1)}
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão - renderizado via Portal para evitar sobreposição do cabeçalho sticky */}
      {deleteConfirm && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => !isDeleting && setDeleteConfirm(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div style={{ padding: '24px', textAlign: 'center' }}>
              <i className="fas fa-exclamation-triangle" style={{ fontSize: '48px', color: '#ef4444', marginBottom: '16px' }}></i>
              <h3 style={{ marginBottom: '8px' }}>Confirmar Exclusão</h3>
              <p style={{ color: '#6b7280', marginBottom: '24px' }}>
                Tem certeza que deseja excluir o pedido <strong>{deleteConfirm.pedido}</strong>? Esta ação não pode ser desfeita.
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button className="btn btn-outline" onClick={() => setDeleteConfirm(null)} disabled={isDeleting}>Cancelar</button>
                <button className="btn btn-primary" onClick={handleDelete} disabled={isDeleting} style={{ backgroundColor: '#ef4444', borderColor: '#ef4444' }}>
                  {isDeleting ? 'Excluindo...' : 'Excluir'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de Importação */}
      {showImportModal && (
        <ImportarEtiquetasModal
          onClose={() => setShowImportModal(false)}
          onSuccess={handleImportSuccess}
          clientes={clientes}
        />
      )}
    </div>
  );
};

// Componente de card de estatística
const StatCard: React.FC<{ label: string; value: number; color: string; icon: string }> = ({ label, value, color, icon }) => (
  <div style={{
    background: '#fff',
    borderRadius: '8px',
    padding: '14px 16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    borderLeft: `4px solid ${color}`,
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  }}>
    <i className={icon} style={{ color, fontSize: '20px', opacity: 0.8 }}></i>
    <div>
      <div style={{ fontSize: '22px', fontWeight: 700, color: '#111827' }}>{value}</div>
      <div style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>{label}</div>
    </div>
  </div>
);

export default EtiquetasList;
